import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "fileList", "dropZone", "inputActivity"]
  static values = {
    activityId: String,
    inloopId: String,
    uploadUrl: String,
    deleteUrl: String,
    statusUrl: String,
    saveUrl: String,
    preloadedFiles: { type: Array, default: [] },
    readOnly: { type: Boolean, default: false }
  }

  connect() {
    this._pollTimer = null
    this.renderFileList()
    this.dispatchAnalysisSourceState()
    this.maybeStartPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  triggerUpload() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  async handleFileSelect(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    await this.uploadFiles(files)

    // Reset input
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragEnter(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-[#4427BF]", "bg-[#FAFAFB]")
      this.dropZoneTarget.classList.remove("border-[#E4E5E8]")
    }
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-[#4427BF]", "bg-[#FAFAFB]")
      this.dropZoneTarget.classList.add("border-[#E4E5E8]")
    }
  }

  async drop(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-[#4427BF]", "bg-[#FAFAFB]")
      this.dropZoneTarget.classList.add("border-[#E4E5E8]")
    }

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    await this.uploadFiles(files)
  }

  async uploadFiles(files) {
    const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50 MB

    const oversized = files.filter(f => f.size > MAX_FILE_SIZE)
    if (oversized.length > 0) {
      const names = oversized.map(f => `${f.name} (${this.formatFileSize(f.size)})`).join(", ")
      this.showUploadError(`File too large: ${names}. Max 50 MB per file.`)
      return
    }

    const formData = new FormData()
    files.forEach(file => {
      formData.append("analytic_files[]", file)
    })

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        if (data.input_files) {
          this.preloadedFilesValue = data.input_files
          this.renderFileList()
          this.dispatchFileCount()
          this.maybeStartPolling()
        }
      } else {
        const data = await response.json().catch(() => ({}))
        this.showUploadError(data.error || "Failed to upload files. Please try again.")
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showUploadError("Upload failed. Please check your connection and try again.")
    }
  }

  showUploadError(message) {
    const existing = this.element.querySelector("[data-upload-error]")
    if (existing) existing.remove()

    const el = document.createElement("p")
    el.setAttribute("data-upload-error", "")
    el.className = "text-xs text-error-600 flex items-center gap-1"
    el.textContent = message

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.insertAdjacentElement("afterend", el)
    }

    setTimeout(() => el.remove(), 5000)
  }

  async deleteFile(event) {
    const blobId = event.currentTarget.dataset.blobId
    if (!blobId) return

    try {
      const response = await fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ blob_id: blobId })
      })

      if (response.ok) {
        // Remove from preloaded files
        this.preloadedFilesValue = this.preloadedFilesValue.filter(f => f.blob_id !== blobId)
        this.renderFileList()
        this.dispatchFileCount()
      }
    } catch (error) {
      console.error("Delete error:", error)
    }
  }

  renderFileList() {
    if (!this.hasFileListTarget) return

    const files = this.preloadedFilesValue
    if (files.length === 0) {
      this.fileListTarget.innerHTML = ""
      return
    }

    this.fileListTarget.innerHTML = files.map(file => {
      const sizeLabel = this.formatFileSize(file.byte_size)
      const statusBadge = this.fileStatusBadge(file.conversion_status)
      const downloadLink = file.download_url ? `<a href="${file.download_url}" target="_blank" rel="noopener noreferrer" class="text-[#9CA3AF] hover:text-brand-600 flex-shrink-0 transition-colors" title="Download file"><svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></a>` : ""

      return `
        <div class="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-[#E4E5E8] bg-white text-sm">
          <svg class="w-4 h-4 text-[#6B7280] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/>
          </svg>
          <span class="text-[#374151] truncate flex-1 min-w-0">${file.filename}</span>
          <span class="text-[#9CA3AF] text-xs flex-shrink-0">${sizeLabel}</span>
          ${statusBadge}
          ${downloadLink}
          ${this.readOnlyValue ? "" : `
            <button type="button" class="text-[#9CA3AF] hover:text-[#DC2626] flex-shrink-0 transition-colors"
                    data-action="click->ai-analytic-config#deleteFile"
                    data-blob-id="${file.blob_id}">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          `}
        </div>
      `
    }).join("")
  }

  fileStatusBadge(status) {
    if (status === "ready") {
      return `<span class="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[#ECFDF3] border border-[#ABEFC6] text-[#067647]">
        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
        Ready
      </span>`
    }
    if (status === "failed") {
      return `<span class="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626]">
        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        Failed
      </span>`
    }
    // pending
    return `<span class="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[#FFF7ED] border border-[#FED7AA] text-[#C2410C]">
      <svg class="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
      Processing...
    </span>`
  }

  maybeStartPolling() {
    const hasPending = this.preloadedFilesValue.some(f => f.conversion_status === "pending")
    if (hasPending) this.startPolling()
  }

  startPolling() {
    if (this._pollTimer) return
    this._pollTimer = setInterval(() => this.pollStatuses(), 3000)
  }

  stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer)
      this._pollTimer = null
    }
  }

  async pollStatuses() {
    if (!this.hasStatusUrlValue) return

    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return

      const data = await response.json()

      // Detect transitions from pending → failed and show toast
      const prevStatuses = Object.fromEntries(
        (this.preloadedFilesValue || []).map(f => [f.blob_id, f.conversion_status])
      )
      data.files.forEach(f => {
        if (f.conversion_status === "failed" && prevStatuses[f.blob_id] === "pending") {
          this.showToastError(f)
        }
      })

      this.preloadedFilesValue = data.files
      this.renderFileList()
      this.dispatchFileCount()

      const stillPending = data.files.some(f => f.conversion_status === "pending")
      if (!stillPending) this.stopPolling()
    } catch (e) {
      // silently ignore poll errors
    }
  }

  showToastError(file) {
    const toast = window.toastController
    if (!toast) return

    const isEncoding = file.conversion_error?.includes("encoding") || file.conversion_error?.includes("character")
    const message = isEncoding
      ? `"${file.filename}" could not be processed — unsupported encoding or file format. Try re-saving the file as UTF-8 or converting it to a different format.`
      : `"${file.filename}" failed to process. Please remove and re-upload the file.`

    toast.error(message)
  }

  dispatchFileCount() {
    this.dispatchAnalysisSourceState()
  }

  getInputActivityIds() {
    if (this.hasInputActivityTarget) {
      return this.inputActivityTargets
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value)
    }
    return []
  }

  async saveInputActivities() {
    if (!this.hasSaveUrlValue) return

    try {
      await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          analytic_input_activity_ids: this.getInputActivityIds()
        })
      })
      this.dispatchAnalysisSourceState()
    } catch (error) {
      console.error("[ai-analytic-config] Failed to save input activities:", error)
    }
  }

  selectedOutcomeCount() {
    if (!this.hasInputActivityTarget) return 0

    return this.inputActivityTargets.filter((checkbox) => {
      return checkbox.checked && checkbox.dataset.hasOutcome === "true"
    }).length
  }

  dispatchAnalysisSourceState() {
    const fileCount = this.preloadedFilesValue.length
    const selectedOutcomeCount = this.selectedOutcomeCount()

    window.dispatchEvent(new CustomEvent("analytic-files-changed", {
      detail: {
        count: fileCount,
        selectedOutcomeCount: selectedOutcomeCount
      }
    }))
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
